import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import * as ContentActions from '../../../../../actions/content'
import { ACL, colors } from '../../../../../constants'
import { Confirmation, Headings } from '../../../../../components'
import Button, { Sizes as ButtonSizes } from '../../../../../components/Button'
import Padding, { Sizes as PaddingSizes } from '../../../../../components/Padding'
import * as MediaSelectors from '../../../../../reducers/content/media/selectors'
import Media, { MediaTestingStatusValues } from '../../../../../types/Media'
import { EstateUtils } from '../../../../../utils'

export default function MediaTesting(): React.ReactElement {
    const selectedMedia: Media = useSelector(MediaSelectors.selectedMedia)
    const [description, setDescription] = React.useState('')

    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { id: mediaId, is_tested: status, testing_description: testingDescription }: Media = selectedMedia || {}

    const canApproveMedia: boolean = useSelector((state) => EstateUtils.hasPermission(state, { acls: [ACL.APPROVE_MEDIA] }))

    const onTestOkMediaClick = (): void => {
        dispatch(ContentActions.testMedia(mediaId, true))
    }

    const onTestErrorMediaClick = (): void => {
        dispatch(ContentActions.testMedia(mediaId, false, { description }))
    }

    const onChangeDescription = (event: React.FormEvent) => {
        const target = event.target as HTMLTextAreaElement
        const value = target.value as string
        setDescription(value)
    }

    switch (status) {
        /**
         * Pending
         */
        case MediaTestingStatusValues.Pending: {
            return (
                <Padding top size={PaddingSizes.md}>
                    <Padding bottom size={PaddingSizes.xs}>
                        <FontAwesomeIcon icon="exclamation-circle" />
                        &nbsp;
                        <Headings.H3 bold>{t('media-testing-pending')}</Headings.H3>
                    </Padding>
                    {canApproveMedia && (
                        <>
                            <Padding autoWidth inline right size={PaddingSizes.sm}>
                                <Confirmation
                                    confirmation={t('media-testing-approve-confirmation')}
                                    header={t('media-testing-approve-confirmation', {
                                        context: 'title',
                                    })}
                                    ok={t('media-testing-approve')}
                                    cancel={t('cancel')}
                                    onCancel={() => null}
                                    onConfirm={onTestOkMediaClick}
                                >
                                    <Button size={ButtonSizes.md}>{t('media-testing-approve')}</Button>
                                </Confirmation>
                            </Padding>
                            <Confirmation
                                confirmation={t('media-testing-reject-confirmation')}
                                header={t('media-testing-reject-confirmation', {
                                    context: 'title',
                                })}
                                ok={t('media-testing-reject')}
                                cancel={t('cancel')}
                                onCancel={() => setDescription('')}
                                onChangeText={onChangeDescription}
                                onConfirm={onTestErrorMediaClick}
                                text={description}
                                withTextArea
                            >
                                <Button size={ButtonSizes.md} theme="danger">
                                    {t('media-testing-reject')}
                                </Button>
                            </Confirmation>
                        </>
                    )}
                </Padding>
            )
        }
        /**
         * Rejected
         */
        case MediaTestingStatusValues.Rejected: {
            return (
                <Padding top bottom size={PaddingSizes.sm}>
                    <Padding bottom size={PaddingSizes.sm}>
                        <Headings.H3 color={colors.reddark}>{t('media-testing-rejected')}</Headings.H3>
                    </Padding>
                    <Headings.H3 italic>{testingDescription}</Headings.H3>
                </Padding>
            )
        }
        /**
         * Tested OK
         */
        default: {
            return (
                <Padding top size={PaddingSizes.sm}>
                    <Headings.H3 color={colors.grey}>
                        <FontAwesomeIcon icon="check" />
                        &nbsp;
                        {t('media-testing-tested')}
                    </Headings.H3>
                </Padding>
            )
        }
    }
}
